import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import GuideLayout from '../../components/layouts/GuideLayout';
import { guideAPI, stripeConnectAPI, userAPI, authAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useGuideNotifications } from '../../hooks/useGuideNotifications';
import PushNotificationSettings from '../../components/PushNotificationSettings';
import GuideAvailabilityCalendar from '../../components/GuideAvailabilityCalendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  MapPin, 
  Calendar, 
  Users, 
  Phone, 
  Mail,
  Clock,
  ChevronRight,
  DollarSign,
  FileText,
  Building2,
  CreditCard,
  Banknote,
  Camera,
  Upload,
  Image,
  Paperclip,
  Eye,
  EyeOff,
  Download,
  User,
  IdCard,
  Globe,
  Award,
  Edit,
  Save,
  X,
  Timer,
  Loader2,
  ExternalLink,
  Wallet,
  Lock,
  Shield,
  Volume2,
  VolumeX,
  BellRing,
  Smartphone,
  ShieldCheck,
  ShieldOff,
  QrCode,
  KeyRound,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GuideDashboard = () => {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') || 'requests';
  const stripeStatus = searchParams.get('stripe');
  const { updateUser } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [profile, setProfile] = useState(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    license_number: '',
    guide_city: '',
    guide_country: '',
    monument_badge_no: '',
    cutoff_hours: 1,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [setting2FA, setSetting2FA] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [manualSecret, setManualSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  
  // Stripe Connect state
  const [stripeAccount, setStripeAccount] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem('guideNotificationsEnabled') !== 'false';
  });
  const pollingIntervalRef = useRef(null);

  // Use the notification hook
  const {
    notificationPermission,
    requestPermission,
    enableAudio,
    testSound,
  } = useGuideNotifications(
    notificationsEnabled ? requests : [],
    (newRequest) => {
      // Callback when user clicks on notification
      console.log('User clicked notification for request:', newRequest.id);
    }
  );

  // Toggle notifications
  const toggleNotifications = useCallback(async () => {
    if (!notificationsEnabled) {
      // Enable notifications
      enableAudio(); // Initialize audio context
      if (notificationPermission === 'default') {
        const permission = await requestPermission();
        if (permission === 'granted') {
          setNotificationsEnabled(true);
          localStorage.setItem('guideNotificationsEnabled', 'true');
          toast.success('Notifications enabled! You will hear a sound for new requests.');
        } else {
          toast.error('Please allow notifications in your browser settings');
        }
      } else if (notificationPermission === 'granted') {
        setNotificationsEnabled(true);
        localStorage.setItem('guideNotificationsEnabled', 'true');
        toast.success('Notifications enabled!');
      } else {
        toast.error('Notifications are blocked. Please enable them in browser settings.');
      }
    } else {
      // Disable notifications
      setNotificationsEnabled(false);
      localStorage.setItem('guideNotificationsEnabled', 'false');
      toast.info('Notifications disabled');
    }
  }, [notificationsEnabled, notificationPermission, requestPermission, enableAudio]);

  // Polling function for new requests (runs every 30 seconds)
  const pollForRequests = useCallback(async () => {
    if (view !== 'requests') return;
    
    try {
      const response = await guideAPI.getRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Polling failed:', error);
    }
  }, [view]);

  // Set up polling interval
  useEffect(() => {
    // Initial load
    loadData();
    loadProfile();
    loadStripeStatus();
    
    // Handle Stripe return
    if (stripeStatus === 'complete') {
      toast.success('Stripe account setup complete!');
      loadStripeStatus();
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (stripeStatus === 'refresh') {
      toast.info('Please complete your Stripe account setup');
      handleStripeOnboarding();
    }

    // Set up polling for new requests (every 30 seconds)
    if (view === 'requests') {
      pollingIntervalRef.current = setInterval(pollForRequests, 30000);
    }

    // Cleanup polling on unmount or view change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [view, stripeStatus, pollForRequests]);

  const loadStripeStatus = async () => {
    try {
      const res = await stripeConnectAPI.getStatus();
      setStripeAccount(res.data);
    } catch (error) {
      console.error('Failed to load Stripe status', error);
    }
  };

  const handleStripeOnboarding = async () => {
    setStripeLoading(true);
    try {
      const res = await stripeConnectAPI.createAccount();
      // Redirect to Stripe onboarding
      window.location.href = res.data.onboarding_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    setStripeLoading(true);
    try {
      const res = await stripeConnectAPI.getDashboardLink();
      window.open(res.data.dashboard_url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    } finally {
      setStripeLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const res = await guideAPI.getProfile();
      setProfile(res.data);
      setProfileForm({
        name: res.data.name || '',
        phone: res.data.phone || '',
        license_number: res.data.license_number || '',
        guide_city: res.data.guide_city || '',
        guide_country: res.data.guide_country || '',
        monument_badge_no: res.data.monument_badge_no || '',
        cutoff_hours: res.data.cutoff_hours || 1,
      });
      setTwoFactorEnabled(res.data.totp_enabled || false);
    } catch (error) {
      console.error('Failed to load profile', error);
    }
  };

  // 2FA Functions
  const handleSetup2FA = async () => {
    setSetting2FA(true);
    try {
      const response = await authAPI.setup2FA();
      setQrCodeData(response.data.qr_code_url);
      setManualSecret(response.data.manual_entry_key);
      setShow2FASetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying2FA(true);
    try {
      await authAPI.verify2FA(verificationCode);
      setTwoFactorEnabled(true);
      setShow2FASetup(false);
      setVerificationCode('');
      setQrCodeData(null);
      setManualSecret('');
      updateUser({ totp_enabled: true });
      toast.success('Two-factor authentication enabled successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error('Please enter your password');
      return;
    }
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setDisabling2FA(true);
    try {
      await authAPI.disable2FA(disablePassword, disableCode);
      setTwoFactorEnabled(false);
      setShow2FADisable(false);
      setDisableCode('');
      setDisablePassword('');
      updateUser({ totp_enabled: false });
      toast.success('Two-factor authentication has been disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(passwordForm.newPassword)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(passwordForm.newPassword)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(passwordForm.newPassword)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await userAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (view === 'history') {
        const response = await guideAPI.getHistory();
        setHistory(response.data);
      } else {
        const response = await guideAPI.getRequests();
        setRequests(response.data);
      }
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    setActionLoading(requestId);
    try {
      const response = await guideAPI.acceptRequest(requestId);
      toast.success('Request accepted! Customer info available now.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (requestId) => {
    if (!window.confirm('Decline this request?')) return;
    
    setActionLoading(requestId);
    try {
      await guideAPI.declineRequest(requestId, 'Not available');
      toast.success('Request declined');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (requestId) => {
    setActionLoading(requestId);
    try {
      await guideAPI.completeRequest(requestId);
      toast.success('Tour marked as completed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFileSelect = (requestId, files) => {
    setSelectedFiles(prev => ({ ...prev, [requestId]: Array.from(files) }));
  };

  const handleUploadProof = async (requestId) => {
    const files = selectedFiles[requestId];
    if (!files || files.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }
    
    setUploadingProof(requestId);
    try {
      await guideAPI.uploadProofPhotos(requestId, files);
      toast.success('Proof photos uploaded successfully!');
      setSelectedFiles(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingProof(null);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploadingProfilePhoto(true);
    try {
      await guideAPI.uploadProfilePhoto(file);
      toast.success('Profile photo updated!');
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile photo');
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await guideAPI.updateProfile(profileForm);
      toast.success('Profile updated successfully!');
      setEditingProfile(false);
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const acceptedRequests = requests.filter(r => r.status === 'accepted');

  const getStatusColor = (status) => {
    const colors = {
      pending: 'badge-warning',
      accepted: 'badge-success',
      completed: 'badge-info',
      cancelled: 'badge-neutral',
      declined: 'badge-error',
    };
    return colors[status] || 'badge-neutral';
  };

  const getTitle = () => {
    if (view === 'accepted') return 'Accepted Tours';
    if (view === 'history') return 'Tour History';
    if (view === 'profile') return 'My Profile';
    if (view === 'availability') return 'Availability Calendar';
    return 'New Requests';
  };

  // Profile Photo URL helper
  const getProfilePhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    const filename = photoPath.split('/').pop();
    return `${BACKEND_URL}/api/public/profile-photos/${filename}`;
  };

  return (
    <GuideLayout title={getTitle()}>
      {/* Profile Section */}
      {view === 'profile' && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <div className="relative">
                {profile?.profile_photo_url ? (
                  <img 
                    src={getProfilePhotoUrl(profile.profile_photo_url)} 
                    alt="Profile" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-indigo-100"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center border-4 border-indigo-50">
                    <User className="w-12 h-12 text-indigo-400" />
                  </div>
                )}
                <label className="absolute bottom-0 right-0 bg-indigo-600 text-white p-2 rounded-full cursor-pointer hover:bg-indigo-700 transition-colors">
                  <Camera className="w-4 h-4" />
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleProfilePhotoUpload}
                    disabled={uploadingProfilePhoto}
                  />
                </label>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900">{profile?.name || 'Guide'}</h2>
                <p className="text-slate-500">{profile?.email}</p>
                <div className="mt-4 flex gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{history.length}</p>
                    <p className="text-xs text-slate-500">Completed Tours</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">{acceptedRequests.length}</p>
                    <p className="text-xs text-slate-500">Active Tours</p>
                  </div>
                </div>
              </div>
            </div>
            {uploadingProfilePhoto && (
              <p className="text-sm text-indigo-600 mt-4">Uploading photo...</p>
            )}
            
            {/* Basic Info Section */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Basic Information</h3>
              </div>
              
              {editingProfile ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      Full Name
                    </Label>
                    <Input
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      Phone Number
                    </Label>
                    <Input
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{profile?.name || 'Not set'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{profile?.phone || 'Not set'}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Credentials Section */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">Professional Credentials</h3>
                {!editingProfile ? (
                  <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingProfile(false)}>
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveProfile} disabled={savingProfile}>
                      <Save className="w-4 h-4 mr-1" />
                      {savingProfile ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                )}
              </div>
              
              {editingProfile ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <IdCard className="w-4 h-4 text-slate-500" />
                      License Number
                    </Label>
                    <Input
                      value={profileForm.license_number}
                      onChange={(e) => setProfileForm({ ...profileForm, license_number: e.target.value })}
                      placeholder="Enter your license number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      City
                    </Label>
                    <Input
                      value={profileForm.guide_city}
                      onChange={(e) => setProfileForm({ ...profileForm, guide_city: e.target.value })}
                      placeholder="Enter your city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-500" />
                      Country Issued
                    </Label>
                    <Input
                      value={profileForm.guide_country}
                      onChange={(e) => setProfileForm({ ...profileForm, guide_country: e.target.value })}
                      placeholder="Enter country where license was issued"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-slate-500" />
                      Monument Badge No. (Optional)
                    </Label>
                    <Input
                      value={profileForm.monument_badge_no}
                      onChange={(e) => setProfileForm({ ...profileForm, monument_badge_no: e.target.value })}
                      placeholder="Enter monument badge number if any"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="flex items-center gap-2">
                      <Timer className="w-4 h-4 text-slate-500" />
                      Cut-off Timer (Minimum Notice)
                    </Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="1"
                        max="72"
                        value={profileForm.cutoff_hours}
                        onChange={(e) => setProfileForm({ ...profileForm, cutoff_hours: parseInt(e.target.value) || 1 })}
                        className="w-24"
                      />
                      <span className="text-sm text-slate-600">hours before tour start</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      You won&apos;t receive requests for tours starting within this time window. Range: 1-72 hours.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <IdCard className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-xs text-slate-500">License Number</p>
                      <p className="font-medium">{profile?.license_number || <span className="text-slate-400 italic">Not set</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-xs text-slate-500">City</p>
                      <p className="font-medium">{profile?.guide_city || <span className="text-slate-400 italic">Not set</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Globe className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-xs text-slate-500">Country Issued</p>
                      <p className="font-medium">{profile?.guide_country || <span className="text-slate-400 italic">Not set</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Award className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-xs text-slate-500">Monument Badge No.</p>
                      <p className="font-medium">{profile?.monument_badge_no || <span className="text-slate-400 italic">Not set</span>}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg md:col-span-2">
                    <Timer className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-slate-500">Cut-off Timer (Minimum Notice)</p>
                      <p className="font-medium text-amber-700">{profile?.cutoff_hours || 1} hour{(profile?.cutoff_hours || 1) !== 1 ? 's' : ''} before tour</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Stripe Connect Section */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-indigo-600" />
                Payment Setup (Stripe Connect)
              </h3>
              
              {stripeAccount?.onboarding_complete ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Stripe Account Connected</p>
                        <p className="text-sm text-green-600">You can receive online payments directly to your bank account</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleStripeDashboard}
                      disabled={stripeLoading}
                      className="border-green-300 text-green-700 hover:bg-green-100"
                      data-testid="stripe-dashboard-btn"
                    >
                      {stripeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                      Stripe Dashboard
                    </Button>
                  </div>
                </div>
              ) : stripeAccount?.has_account ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-6 h-6 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Setup Incomplete</p>
                        <p className="text-sm text-amber-600">Complete your Stripe account setup to receive payments</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleStripeOnboarding}
                      disabled={stripeLoading}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="stripe-continue-btn"
                    >
                      {stripeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                      Continue Setup
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-6 h-6 text-slate-500" />
                      <div>
                        <p className="font-medium text-slate-800">No Payment Account</p>
                        <p className="text-sm text-slate-500">Connect Stripe to receive online tour payments (90% of fee, 10% platform commission)</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleStripeOnboarding}
                      disabled={stripeLoading}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="stripe-setup-btn"
                    >
                      {stripeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-1" />}
                      Setup Stripe
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Change Password Section */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                Change Password
              </h3>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-slate-400" />
                      Current Password
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        placeholder="Current password"
                        required
                        data-testid="guide-current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      placeholder="New password"
                      required
                      data-testid="guide-new-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm Password</Label>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      required
                      data-testid="guide-confirm-password"
                    />
                  </div>
                </div>
                
                {/* Password requirements hints */}
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className={`px-2 py-1 rounded ${passwordForm.newPassword.length >= 8 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    8+ chars
                  </span>
                  <span className={`px-2 py-1 rounded ${/[A-Z]/.test(passwordForm.newPassword) ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    Uppercase
                  </span>
                  <span className={`px-2 py-1 rounded ${/[a-z]/.test(passwordForm.newPassword) ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    Lowercase
                  </span>
                  <span className={`px-2 py-1 rounded ${/[0-9]/.test(passwordForm.newPassword) ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    Number
                  </span>
                </div>
                
                <Button 
                  type="submit" 
                  variant="outline"
                  disabled={changingPassword}
                  data-testid="guide-change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Two-Factor Authentication Section */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-indigo-600" />
                Two-Factor Authentication (2FA)
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Add an extra layer of security by requiring a verification code from your authenticator app when signing in.
              </p>
              
              {twoFactorEnabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <ShieldCheck className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">2FA is Enabled</p>
                      <p className="text-sm text-green-700">Your account is protected with two-factor authentication.</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => setShow2FADisable(true)}
                    data-testid="guide-disable-2fa-btn"
                  >
                    <ShieldOff className="w-4 h-4 mr-2" />
                    Disable Two-Factor Authentication
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">2FA is Not Enabled</p>
                      <p className="text-sm text-amber-700">Enable two-factor authentication to secure your account.</p>
                    </div>
                  </div>
                  <Button
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleSetup2FA}
                    disabled={setting2FA}
                    data-testid="guide-enable-2fa-btn"
                  >
                    {setting2FA ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Enable Two-Factor Authentication
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Push Notifications Section */}
            <div className="border-t pt-6">
              <PushNotificationSettings />
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Notification Settings Card - Only show on requests view */}
      {view === 'requests' && (
        <Card className="mb-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  notificationsEnabled ? 'bg-indigo-600' : 'bg-slate-300'
                }`}>
                  {notificationsEnabled ? (
                    <BellRing className="w-5 h-5 text-white" />
                  ) : (
                    <VolumeX className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Sound Notifications</h3>
                  <p className="text-sm text-slate-500">
                    {notificationsEnabled 
                      ? 'You will hear a sound when new requests arrive' 
                      : 'Enable to get notified of new requests'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationsEnabled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={testSound}
                    className="text-indigo-600 hover:bg-indigo-100"
                    data-testid="test-sound-btn"
                  >
                    <Volume2 className="w-4 h-4 mr-1" />
                    Test
                  </Button>
                )}
                <Button
                  variant={notificationsEnabled ? "default" : "outline"}
                  size="sm"
                  onClick={toggleNotifications}
                  className={notificationsEnabled ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                  data-testid="toggle-notifications-btn"
                >
                  {notificationsEnabled ? (
                    <>
                      <Volume2 className="w-4 h-4 mr-1" />
                      Enabled
                    </>
                  ) : (
                    <>
                      <VolumeX className="w-4 h-4 mr-1" />
                      Enable
                    </>
                  )}
                </Button>
              </div>
            </div>
            {notificationPermission === 'denied' && (
              <p className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Browser notifications are blocked. Please enable them in your browser settings to receive alerts.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : view === 'profile' ? (
        <div className="text-center py-8 text-slate-500">
          <p>Your profile information is displayed above.</p>
        </div>
      ) : view === 'availability' ? (
        <GuideAvailabilityCalendar />
      ) : view === 'history' ? (
        // History View - Enhanced with full tour details
        history.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No tour history</h3>
            <p className="text-slate-500 mt-1">Completed tours will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map(request => (
              <Card key={request.id} className={`border-l-4 ${
                request.status === 'completed' ? 'border-l-emerald-500' : 
                request.status === 'cancelled' ? 'border-l-red-500' : 'border-l-slate-400'
              }`} data-testid={`history-${request.id}`}>
                <CardContent className="p-4">
                  {/* Header with Status and Date */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(request.status)}>{request.status}</Badge>
                      {request.payment_method === 'online' ? (
                        <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Online
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                          <Banknote className="w-3 h-3" />
                          Cash
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm text-slate-500">
                      {formatDateTime(request.requested_start_at)}
                    </span>
                  </div>
                  
                  {/* Company Name */}
                  {request.member_company_name && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 mb-3">
                      <p className="text-sm text-indigo-800 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span className="font-medium">{request.member_company_name}</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Location */}
                  <h3 className="font-semibold text-lg mb-2">
                    {request.city_name}{request.category_name ? ` - ${request.category_name}` : ''}
                  </h3>
                  
                  {request.monument_name && (
                    <p className="text-slate-600 flex items-center gap-1 mb-2">
                      <MapPin className="w-4 h-4" />
                      {request.monument_name}
                    </p>
                  )}
                  
                  {/* Meeting Point */}
                  <p className="text-slate-600 flex items-center gap-1 mb-3">
                    <MapPin className="w-4 h-4" />
                    Meeting: {request.meeting_point}
                  </p>
                  
                  {/* Tour Details Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      <span>{request.duration_hours}h tour</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span>{request.customer_info?.pax || 1} guests</span>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-600 font-semibold">
                      <DollarSign className="w-4 h-4" />
                      <span>€{request.guide_fee_eur}</span>
                    </div>
                    {request.payment_status && (
                      <div className="flex items-center gap-2">
                        <Badge className={
                          request.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          request.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {request.payment_status}
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : view === 'accepted' ? (
        // Accepted View
        acceptedRequests.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No accepted tours</h3>
            <p className="text-slate-500 mt-1">Accept requests to see them here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {acceptedRequests.map(request => (
              <Card key={request.id} className="border-l-4 border-l-emerald-500" data-testid={`accepted-${request.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="badge-success">Accepted</Badge>
                      {request.payment_method === 'online' ? (
                        <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Online
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                          <Banknote className="w-3 h-3" />
                          Cash
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      {formatDateTime(request.requested_start_at)}
                    </span>
                  </div>
                  
                  {/* Company Name */}
                  {request.member_company_name && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 mb-3">
                      <p className="text-sm text-indigo-800 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span className="font-medium">{request.member_company_name}</span>
                      </p>
                    </div>
                  )}
                  
                  <h3 className="font-semibold text-lg mb-2">
                    {request.city_name}{request.category_name ? ` - ${request.category_name}` : ''}
                  </h3>
                  
                  {request.monument_name && (
                    <p className="text-slate-600 flex items-center gap-1 mb-2">
                      <MapPin className="w-4 h-4" />
                      {request.monument_name}
                    </p>
                  )}
                  
                  <p className="text-slate-600 flex items-center gap-1 mb-3">
                    <Calendar className="w-4 h-4" />
                    Meeting: {request.meeting_point}
                  </p>
                  
                  {/* Customer Info */}
                  {request.customer_info && Object.keys(request.customer_info).length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3 mb-3">
                      <h4 className="font-medium text-sm mb-2">Customer Details</h4>
                      {request.customer_info.name && (
                        <p className="text-sm flex items-center gap-2 mb-1">
                          <Users className="w-3 h-3 text-slate-400" />
                          {request.customer_info.name} ({request.customer_info.pax || 1} pax)
                        </p>
                      )}
                      {request.customer_info.phone && (
                        <a 
                          href={`tel:${request.customer_info.phone}`}
                          className="text-sm flex items-center gap-2 mb-1 text-indigo-600"
                        >
                          <Phone className="w-3 h-3" />
                          {request.customer_info.phone}
                        </a>
                      )}
                      {request.customer_info.email && (
                        <a 
                          href={`mailto:${request.customer_info.email}`}
                          className="text-sm flex items-center gap-2 text-indigo-600"
                        >
                          <Mail className="w-3 h-3" />
                          {request.customer_info.email}
                        </a>
                      )}
                      {request.customer_info.notes && (
                        <p className="text-sm text-slate-500 mt-2 italic">
                          &ldquo;{request.customer_info.notes}&rdquo;
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Experience Attachments - Only visible after accepting */}
                  {request.experience_attachments && request.experience_attachments.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2 text-blue-800">
                        <Paperclip className="w-4 h-4" />
                        Experience Tickets ({request.experience_attachments.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {request.experience_attachments.map((attachment, idx) => {
                          const filename = attachment.url.split('/').pop();
                          const isPdf = attachment.type === 'application/pdf';
                          return (
                            <div key={idx} className="flex gap-1">
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => guideAPI.viewExperienceAttachment(filename)}
                                className="flex items-center gap-1 bg-white border-blue-300 text-blue-700 hover:bg-blue-100 text-xs"
                              >
                                {isPdf ? <FileText className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                {attachment.filename || `File ${idx + 1}`}
                              </Button>
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => guideAPI.downloadExperienceAttachment(filename, attachment.filename)}
                                className="flex items-center gap-1 bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Proof of Service Upload - Required for Online Payments */}
                  {request.payment_method === 'online' && (
                    <div className={`rounded-lg p-3 mb-3 ${
                      request.proof_photos && request.proof_photos.length > 0 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                        <Camera className="w-4 h-4" />
                        Proof of Service
                        {request.proof_photos && request.proof_photos.length > 0 ? (
                          <Badge className="bg-green-600 text-white text-xs">Uploaded</Badge>
                        ) : (
                          <Badge className="bg-amber-600 text-white text-xs">Required</Badge>
                        )}
                      </h4>
                      
                      {request.proof_photos && request.proof_photos.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {request.proof_photos.map((url, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-white px-2 py-1 rounded text-xs text-green-700">
                              <Image className="w-3 h-3" />
                              Photo {idx + 1}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-amber-700">
                            Upload photo(s) of the group or voucher to mark as complete.
                          </p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleFileSelect(request.id, e.target.files)}
                              className="flex-1 text-xs"
                              data-testid={`proof-file-input-${request.id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUploadProof(request.id)}
                              disabled={uploadingProof === request.id || !selectedFiles[request.id]?.length}
                              className="bg-amber-600 hover:bg-amber-700"
                              data-testid={`upload-proof-btn-${request.id}`}
                            >
                              {uploadingProof === request.id ? (
                                'Uploading...'
                              ) : (
                                <>
                                  <Upload className="w-3 h-3 mr-1" />
                                  Upload
                                </>
                              )}
                            </Button>
                          </div>
                          {selectedFiles[request.id]?.length > 0 && (
                            <p className="text-xs text-slate-600">
                              {selectedFiles[request.id].length} file(s) selected
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleComplete(request.id)}
                    disabled={
                      actionLoading === request.id || 
                      (!request.proof_photos || request.proof_photos.length === 0)
                    }
                    data-testid={`complete-btn-${request.id}`}
                  >
                    {actionLoading === request.id ? 'Processing...' : 'Mark as Completed'}
                  </Button>
                  {(!request.proof_photos || request.proof_photos.length === 0) && (
                    <p className="text-xs text-amber-600 text-center mt-2 bg-amber-50 p-2 rounded">
                      ⚠️ Please upload proof of service before completing (required for all tours)
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : (
        // Pending Requests View
        pendingRequests.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No new requests</h3>
            <p className="text-slate-500 mt-1">New guide requests will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingRequests.map(request => (
              <Card key={request.id} className="border-l-4 border-l-amber-500" data-testid={`request-${request.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="badge-warning">New Request</Badge>
                      {request.payment_method === 'online' ? (
                        <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Online
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                          <Banknote className="w-3 h-3" />
                          Cash
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      {formatDateTime(request.created_at)}
                    </span>
                  </div>
                  
                  {/* Company Name */}
                  {request.member_company_name && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 mb-3">
                      <p className="text-sm text-indigo-800 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span className="font-medium">{request.member_company_name}</span>
                      </p>
                    </div>
                  )}
                  
                  <h3 className="font-semibold text-lg mb-2">
                    {request.city_name}{request.category_name ? ` - ${request.category_name}` : ''}
                  </h3>
                  
                  {request.monument_name && (
                    <p className="text-slate-600 flex items-center gap-1 mb-2">
                      <MapPin className="w-4 h-4" />
                      {request.monument_name}
                    </p>
                  )}
                  
                  <p className="text-slate-600 flex items-center gap-1 mb-2">
                    <Calendar className="w-4 h-4" />
                    {formatDateTime(request.requested_start_at)}
                  </p>
                  
                  <p className="text-slate-600 flex items-center gap-1 mb-3">
                    <ChevronRight className="w-4 h-4" />
                    Meeting: {request.meeting_point}
                  </p>

                  {/* Experience Details - Fee, Duration, Description */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3">
                    <h4 className="font-medium text-emerald-800 text-sm mb-2">Experience Details</h4>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        <span className="text-lg font-bold text-emerald-700">
                          €{request.guide_fee_eur?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-emerald-600" />
                        <span className="text-emerald-700">
                          {request.duration_hours || 0} hour{request.duration_hours !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    {request.experience_description && (
                      <div className="flex items-start gap-2 mt-2 pt-2 border-t border-emerald-200">
                        <FileText className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-emerald-800">{request.experience_description}</p>
                      </div>
                    )}
                  </div>

                  {/* Number of people */}
                  {request.customer_info?.pax && (
                    <p className="text-slate-600 flex items-center gap-1 mb-3">
                      <Users className="w-4 h-4" />
                      {request.customer_info.pax} {request.customer_info.pax === 1 ? 'person' : 'people'}
                    </p>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => handleAccept(request.id)}
                      disabled={actionLoading === request.id}
                      data-testid={`accept-btn-${request.id}`}
                    >
                      {actionLoading === request.id ? 'Processing...' : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleDecline(request.id)}
                      disabled={actionLoading === request.id}
                      data-testid={`decline-btn-${request.id}`}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
      
      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-600" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {qrCodeData && (
              <div className="flex justify-center">
                <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
                  <img 
                    src={qrCodeData} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                    data-testid="guide-2fa-qr-code"
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <p className="text-sm text-slate-600 font-medium">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-slate-100 rounded text-sm font-mono break-all">
                  {manualSecret}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(manualSecret);
                    toast.success('Secret copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="guide-verification-code" className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-400" />
                Enter Verification Code
              </Label>
              <Input
                id="guide-verification-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="guide-2fa-verification-code"
              />
              <p className="text-xs text-slate-500">Enter the 6-digit code from your authenticator app</p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShow2FASetup(false);
                  setVerificationCode('');
                  setQrCodeData(null);
                  setManualSecret('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleVerify2FA}
                disabled={verifying2FA || verificationCode.length !== 6}
                data-testid="guide-verify-2fa-btn"
              >
                {verifying2FA ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify & Enable
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <AlertDialog open={show2FADisable} onOpenChange={setShow2FADisable}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldOff className="w-5 h-5" />
              Disable Two-Factor Authentication
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the extra security layer from your account. Enter your password and the verification code from your authenticator app to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="guide-disable-password" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Password
              </Label>
              <Input
                id="guide-disable-password"
                type="password"
                placeholder="Enter your password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                data-testid="guide-disable-2fa-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="guide-disable-code" className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-400" />
                Verification Code
              </Label>
              <Input
                id="guide-disable-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="guide-disable-2fa-code"
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDisableCode(''); setDisablePassword(''); }}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={disabling2FA || disableCode.length !== 6 || !disablePassword}
              data-testid="guide-confirm-disable-2fa-btn"
            >
              {disabling2FA ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable 2FA'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GuideLayout>
  );
};

export default GuideDashboard;
